import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { defaultCommunitySettings, type CommunitySettingsData } from '@/mocks/adminSettingsCommunity';

type SaveStatus = 'idle' | 'saving' | 'saved';

export default function AdminSettingsCommunityPage() {
  const [settings, setSettings] = useState<CommunitySettingsData>(defaultCommunitySettings);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const handleSave = useCallback(() => {
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('saved');
      SwalAlert.toast('Configuración guardada correctamente', 'success');
      setTimeout(() => setSaveStatus('idle'), 1500);
    }, 600);
  }, []);

  const toggleOpcion = (key: keyof CommunitySettingsData['opciones']) => {
    setSettings((prev) => ({
      ...prev,
      opciones: { ...prev.opciones, [key]: !prev.opciones[key] },
    }));
  };

  const toggleNotificacion = (key: keyof CommunitySettingsData['notificaciones']) => {
    setSettings((prev) => ({
      ...prev,
      notificaciones: { ...prev.notificaciones, [key]: !prev.notificaciones[key] },
    }));
  };

  const setPrivacidad = (value: CommunitySettingsData['privacidad']) => {
    setSettings((prev) => ({ ...prev, privacidad: value }));
  };

  const opcionesItems = [
    {
      key: 'permitirComentarios' as const,
      label: 'Permitir comentarios',
      icon: 'ri-chat-3-line',
      color: 'bg-emerald-500',
      desc: 'Los seguidores pueden comentar en publicaciones, fotos y videos del perfil de la radio.',
    },
    {
      key: 'permitirReacciones' as const,
      label: 'Permitir reacciones',
      icon: 'ri-heart-line',
      color: 'bg-rose-500',
      desc: 'Habilita likes, corazones y otras reacciones en el contenido de la comunidad.',
    },
    {
      key: 'permitirCompartir' as const,
      label: 'Permitir compartir',
      icon: 'ri-share-forward-line',
      color: 'bg-violet-500',
      desc: 'Los usuarios pueden compartir publicaciones y episodios en sus redes sociales.',
    },
    {
      key: 'permitirSeguidores' as const,
      label: 'Permitir seguidores',
      icon: 'ri-user-add-line',
      color: 'bg-amber-500',
      desc: 'Permite que nuevos oyentes sigan el perfil de la radio y reciban actualizaciones.',
    },
  ];

  const notificacionesItems = [
    {
      key: 'nuevosSeguidores' as const,
      label: 'Nuevos seguidores',
      icon: 'ri-user-follow-line',
      color: 'bg-cyan-500',
      desc: 'Recibís una notificación cada vez que alguien nuevo sigue tu radio.',
    },
    {
      key: 'nuevosComentarios' as const,
      label: 'Nuevos comentarios',
      icon: 'ri-chat-check-line',
      color: 'bg-indigo-500',
      desc: 'Te avisamos cuando alguien comenta en tus publicaciones, fotos o videos.',
    },
    {
      key: 'mensajesCabina' as const,
      label: 'Mensajes de cabina',
      icon: 'ri-broadcast-line',
      color: 'bg-orange-500',
      desc: 'Notificaciones cuando hay mucha actividad o mensajes destacados en la cabina.',
    },
  ];

  const activeFeatures = Object.values(settings.opciones).filter(Boolean).length;
  const activeNotifs = Object.values(settings.notificaciones).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
              <i className="ri-group-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Comunidad</h1>
              <p className="text-foreground-400 text-[9px]">Configuración</p>
            </div>
          </div>
          <Link
            to="/admin/settings"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            Ajustes
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/settings"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xs"></i>
                Configuración
              </Link>
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Configuración de Comunidad</h2>
                <p className="text-foreground-400 text-xs">
                  Gestioná la interacción social y la visibilidad de tu radio
                </p>
              </div>
            </div>
            <Link
              to="/"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line text-xs"></i>
              Ver sitio
            </Link>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8">
            <div className="max-w-[720px] mx-auto">
              {/* Stats mini-row */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-background-50 border border-background-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <i className="ri-check-double-line text-emerald-500 text-sm"></i>
                    </div>
                    <span className="text-[10px] text-foreground-400 font-medium">Funciones activas</span>
                  </div>
                  <p className="text-lg font-bold text-foreground-900">
                    {activeFeatures}
                    <span className="text-foreground-300 text-xs font-normal"> / 4</span>
                  </p>
                </div>
                <div className="bg-background-50 border border-background-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 rounded-lg bg-cyan-100 flex items-center justify-center">
                      <i className="ri-notification-3-line text-cyan-500 text-sm"></i>
                    </div>
                    <span className="text-[10px] text-foreground-400 font-medium">Notificaciones</span>
                  </div>
                  <p className="text-lg font-bold text-foreground-900">
                    {activeNotifs}
                    <span className="text-foreground-300 text-xs font-normal"> / 3</span>
                  </p>
                </div>
              </div>

              {/* ─── SECTION: Opciones ─── */}
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6 mb-5">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-background-200">
                  <div className="w-10 h-10 rounded-xl bg-foreground-950 flex items-center justify-center shrink-0">
                    <i className="ri-settings-3-line text-background-50 text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-foreground-950 font-bold text-sm">Opciones de Comunidad</h3>
                    <p className="text-foreground-400 text-[10px]">Activá o desactivá las funcionalidades sociales de tu perfil</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {opcionesItems.map((item) => (
                    <div
                      key={item.key}
                      className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
                        settings.opciones[item.key]
                          ? 'border-secondary-200 bg-secondary-50/50'
                          : 'border-background-200 bg-background-100/50 opacity-70'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-xl ${item.color} flex items-center justify-center shrink-0 mt-0.5 ${
                          !settings.opciones[item.key] && 'opacity-30'
                        }`}
                      >
                        <i className={`${item.icon} text-background-50 text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-foreground-900">{item.label}</h4>
                          <button
                            onClick={() => toggleOpcion(item.key)}
                            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${
                              settings.opciones[item.key] ? 'bg-secondary-500' : 'bg-background-300'
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${
                                settings.opciones[item.key] ? 'left-[22px]' : 'left-0.5'
                              }`}
                            ></span>
                          </button>
                        </div>
                        <p className="text-foreground-500 text-[10px] leading-relaxed mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ─── SECTION: Privacidad ─── */}
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6 mb-5">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-background-200">
                  <div className="w-10 h-10 rounded-xl bg-foreground-950 flex items-center justify-center shrink-0">
                    <i className="ri-shield-keyhole-line text-background-50 text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-foreground-950 font-bold text-sm">Privacidad</h3>
                    <p className="text-foreground-400 text-[10px]">Controlá quién puede ver e interactuar con tu comunidad</p>
                  </div>
                </div>

                <div className="space-y-2.5">
                  <button
                    onClick={() => setPrivacidad('publica')}
                    className={`w-full flex items-start gap-4 p-4 rounded-xl border transition-all cursor-pointer text-left ${
                      settings.privacidad === 'publica'
                        ? 'border-secondary-200 bg-secondary-50/50'
                        : 'border-background-200 bg-background-50 hover:border-background-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                      settings.privacidad === 'publica' ? 'bg-emerald-500' : 'bg-background-200'
                    }`}>
                      <i className={`ri-global-line text-base ${
                        settings.privacidad === 'publica' ? 'text-background-50' : 'text-foreground-400'
                      }`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-foreground-900">Pública</h4>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          settings.privacidad === 'publica' ? 'border-secondary-500' : 'border-background-300'
                        }`}>
                          {settings.privacidad === 'publica' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-secondary-500"></div>
                          )}
                        </div>
                      </div>
                      <p className="text-foreground-500 text-[10px] leading-relaxed mt-1">
                        Cualquier persona puede ver el perfil, publicaciones y seguir la radio sin necesidad de registrarse.
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={() => setPrivacidad('registrados')}
                    className={`w-full flex items-start gap-4 p-4 rounded-xl border transition-all cursor-pointer text-left ${
                      settings.privacidad === 'registrados'
                        ? 'border-secondary-200 bg-secondary-50/50'
                        : 'border-background-200 bg-background-50 hover:border-background-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                      settings.privacidad === 'registrados' ? 'bg-amber-500' : 'bg-background-200'
                    }`}>
                      <i className={`ri-lock-line text-base ${
                        settings.privacidad === 'registrados' ? 'text-background-50' : 'text-foreground-400'
                      }`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-foreground-900">Solo registrados</h4>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          settings.privacidad === 'registrados' ? 'border-secondary-500' : 'border-background-300'
                        }`}>
                          {settings.privacidad === 'registrados' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-secondary-500"></div>
                          )}
                        </div>
                      </div>
                      <p className="text-foreground-500 text-[10px] leading-relaxed mt-1">
                        Solo usuarios registrados pueden interactuar con la comunidad. Mayor control y menos spam.
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              {/* ─── SECTION: Notificaciones ─── */}
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6 mb-5">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-background-200">
                  <div className="w-10 h-10 rounded-xl bg-foreground-950 flex items-center justify-center shrink-0">
                    <i className="ri-notification-3-line text-background-50 text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-foreground-950 font-bold text-sm">Notificaciones</h3>
                    <p className="text-foreground-400 text-[10px]">Elegí qué alertas querés recibir sobre la actividad de tu comunidad</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {notificacionesItems.map((item) => (
                    <div
                      key={item.key}
                      className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
                        settings.notificaciones[item.key]
                          ? 'border-cyan-200 bg-cyan-50/30'
                          : 'border-background-200 bg-background-100/50 opacity-70'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-xl ${item.color} flex items-center justify-center shrink-0 mt-0.5 ${
                          !settings.notificaciones[item.key] && 'opacity-30'
                        }`}
                      >
                        <i className={`${item.icon} text-background-50 text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-foreground-900">{item.label}</h4>
                          <button
                            onClick={() => toggleNotificacion(item.key)}
                            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${
                              settings.notificaciones[item.key] ? 'bg-cyan-500' : 'bg-background-300'
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${
                                settings.notificaciones[item.key] ? 'left-[22px]' : 'left-0.5'
                              }`}
                            ></span>
                          </button>
                        </div>
                        <p className="text-foreground-500 text-[10px] leading-relaxed mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Save bar */}
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5 flex items-center justify-between">
                <div>
                  <p className="text-foreground-800 text-xs font-semibold">
                    {activeFeatures} de 4 funciones activas
                    <span className="text-foreground-300 mx-2">·</span>
                    {activeNotifs} de 3 notificaciones
                    <span className="text-foreground-300 mx-2">·</span>
                    Perfil {settings.privacidad === 'publica' ? 'público' : 'privado'}
                  </p>
                  <p className="text-foreground-400 text-[10px] mt-0.5">Los cambios se aplican al instante en el perfil público.</p>
                </div>
                <button
                  onClick={handleSave}
                  disabled={saveStatus === 'saving'}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-semibold hover:bg-foreground-800 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap"
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-sm"></i>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line text-sm"></i>
                      Guardar Cambios
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>




    </div>
  );
}